// Pure figures behind the venue Dashboard's headline tiles.

import type { Board, Coverage } from "./api";

/**
 * The market-cap tile's note: how many tokens the summed cap covers. A token
 * counts only when it has a market cap; a priced token with no supply read
 * adds nothing to the total, so it is not counted.
 */
export function marketCapNote(board: Pick<Board, "tokens" | "totals">): string {
  const withCap = board.tokens.filter((t) => t.market_cap !== null && Number.isFinite(t.market_cap)).length;
  return `across ${withCap} of ${board.totals.tokens} tokens`;
}

/**
 * Share of value as a whole percent, rounded down so a near-complete share
 * (0.999) never reads as 100%, and a tiny one reads "under 1%" rather than 0%.
 */
export function coveragePercent(share: number): string {
  if (share >= 1) return "100%";
  const whole = Math.floor(share * 100);
  return whole === 0 && share > 0 ? "under 1%" : `${whole}%`;
}

/**
 * What a tile adds about a total's coverage, or null when there is nothing to
 * add: no coverage reported, nothing measured yet (the tile says "in
 * progress"), or the total measures every token's value. A withheld total
 * (null, under the 90% rule) says how far its measurement got.
 */
export function coverageNote(coverage: Coverage | undefined, total: number | null): string | null {
  if (!coverage || coverage.measured_tokens === 0 || coverage.measured_cap_share === null) return null;
  const share = coverage.measured_cap_share;
  if (total === null) return `measured for ${coveragePercent(share)} of value; a total needs 90%`;
  return share >= 1 ? null : `measured for ${coveragePercent(share)} of value`;
}

/** Whether any token carries this figure, so a tile for it is worth showing. */
export function anyMeasured(coverage: Coverage | undefined, total: number | null): boolean {
  return total !== null || (coverage?.measured_tokens ?? 0) > 0;
}
